import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControlLabel,
  Grid2,
  List,
  Popover,
  styled,
  Typography,
} from "@mui/material";
import {
  PrimaryFormActionButton,
  PrimaryTooltip,
} from "../../../Components/StyledComponent";
import SearchInput from "./SearchInput";
import FilterSelectBox from "./FilterSelectBox";
import CustomCheckbox from "./Checkbox";
import { getSelectedLocationsDisplayLabel } from "../CommonFunctions";
import { useTranslation } from "react-i18next";

const popoverPaperStyles = {
  py: "20px",
  px: "16px",
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  boxShadow:
    "2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)",
};

const ItemRowCheckBoxLabel = styled(FormControlLabel)(({ theme }) => ({
  display: "block",
  marginLeft: 0,

  ".MuiButtonBase-root": {
    padding: 0,
  },

  ".MuiCheckbox-root": {
    padding: 0,
    color: "#A2A2A2 !important",

    "&.Mui-checked": {
      color: "#6D72F6 !important",
    },
    "&.Mui-disabled": {
      color: "#A2A2A2 !important",
    },
  },

  ".MuiSvgIcon-root": {
    width: "20px",
    height: "20px",
  },
}));

/* Component */
const MultiSelectDropdown = ({ selectedOptions, options, label, onChange, defaultAllSelected = false }) => {

  const { t } = useTranslation(["common"]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(selectedOptions);
  const [search, setSearch] = useState("");
  const [displayLabel, setDisplayLabel] = useState("");
  const [tooltipLabel, setTooltipLabel] = useState("");
  const [enableSelectAllOption, setEnableSelectAllOption] = useState(true);
  const [enableDeselectAllOption, setEnableDeselectAllOption] = useState(true);

  const open = Boolean(anchorEl);

  /* ---------- UI ---------- */
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter((item) =>
      item.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const updateLabels = useCallback((selectedItems) => {
    const itemsToUse = selectedItems !== undefined ? selectedItems : selectedOptions;
    const displayLabelsData = getSelectedLocationsDisplayLabel(
      itemsToUse,
      options,
      label
    );
    setDisplayLabel(displayLabelsData?.displayLabel);
    setTooltipLabel(displayLabelsData?.tooltipLabel);
  }, [selectedOptions, options, label]);

  const handleOnApply = () => {
    updateLabels();
    onChange(selected);
    setAnchorEl(null);
  };

  const handleDeselectAll = () => {
    setSelected([]);
  };

  const handleSelectAll = () => {
    setSelected(options);
  };

  const handleSelection = (selectedOption) => {
    setSelected((prev) => {
      const alreadySelected = prev.some((option) => option === selectedOption);

      if (alreadySelected) {
        // remove option
        return prev.filter((option) => option !== selectedOption);
      } else {
        // add option
        return [...prev, selectedOption];
      }
    });
  };

  useEffect(() => {
    setEnableDeselectAllOption(selected.length > 0);
    setEnableSelectAllOption(selected.length < options.length);
  }, [selected]);

  useEffect(() => {
    if (open) {
      // If defaultAllSelected is true and selectedOptions is empty, select all
      if (defaultAllSelected && selectedOptions.length === 0 && options.length > 0) {
        setSelected(options);
      } else {
        setSelected(selectedOptions);
      }
    }
  }, [open, selectedOptions, defaultAllSelected, options]);

  useEffect(() => {
    // If defaultAllSelected is true and selectedOptions is empty, select all
    if (defaultAllSelected && selectedOptions.length === 0 && options.length > 0) {
      setSelected(options);
      updateLabels(options); // Pass the actual selected items
    } else {
      setSelected(selectedOptions);
      updateLabels(selectedOptions); // Pass the actual selected items
    }
  }, [selectedOptions, defaultAllSelected, options, updateLabels]);

  if (options.length === 1) {
    return (
      <Box>
        <FilterSelectBox
          open={false}
          onClick={() => {}}
          label={label}
          displayValue={options[0]}
          disabled={true}
        />
      </Box>
    )
  }

  return (
    <>
      <PrimaryTooltip title={tooltipLabel}>
        <Box>
          <FilterSelectBox
            open={open}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            label={label}
            displayValue={displayLabel}
          />
        </Box>
      </PrimaryTooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: popoverPaperStyles } }}
      >
        <Grid2
          container
          flexDirection={"column"}
          gap={2}
          sx={{ width: "315px" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="fw700s14lh24c3D4B5A"
              sx={{ textTransform: "capitalize" }}
            >
              Select {label}
            </Typography>
            <Box>
              <Typography
                variant="fw500s12lh24c6D72F6"
                sx={{
                  cursor: enableSelectAllOption ? "pointer" : "not-allowed",
                  color: enableSelectAllOption ? "#6D72F6" : "#8F8F8F",
                }}
                onClick={() =>
                  enableSelectAllOption ? handleSelectAll() : null
                }
              >
                {t("actions.selectAll")}
              </Typography>
            </Box>
          </Box>

          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search ${label}`}
          />

          <Box sx={{ height: "200px", overflowY: "auto" }}>
            <List disablePadding>
              {filteredOptions.map((option, index) => {
                return (
                  <Grid2
                    key={index}
                    container
                    gap={2}
                    alignItems="center"
                    sx={{ py: 0.5 }}
                  >
                    <ItemRowCheckBoxLabel
                      key={option}
                      disabled={false}
                      control={
                        <CustomCheckbox
                          checked={selected.some((o) => o === option)}
                          onChange={() => handleSelection(option)}
                        />
                      }
                      label={
                        <Typography
                          variant={
                            true ? "fw400s12lh24c3D4B5A" : "fw400s12lh24c989898"
                          }
                        >
                          {" "}
                          {option}{" "}
                        </Typography>
                      }
                    />
                  </Grid2>
                );
              })}
            </List>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="fw500s12lh24c6D72F6"
                sx={{
                  cursor: enableDeselectAllOption ? "pointer" : "not-allowed",
                  color: enableDeselectAllOption ? "#6D72F6" : "#8F8F8F",
                }}
                onClick={() =>
                  enableDeselectAllOption ? handleDeselectAll() : null
                }
              >
                {t("actions.deselectAll")}
              </Typography>
            </Box>
            <PrimaryFormActionButton
              onClick={handleOnApply}
              disabled={selected.length === 0}
            >
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Grid2>
      </Popover>
    </>
  );
};

export default MultiSelectDropdown;
